import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import {
  TEST_ACTION_LABELS,
  attemptStatusLabel,
  bulkResultMessage,
  recordedSourceLabel,
  type ApplicationStage,
  type AssessmentResultRow,
  type AssessmentResultsView,
  type BulkActionRequest,
  type PipelineActionView,
} from '@nest-recruit/shared';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { selectedKey, selectionApi } from '../../comparison/api/comparison-api';
import { pipelineKey, startBulkAction, undoAction } from '../../pipeline/api/pipeline-api';
import { copyTestLink, resultsKey, useSendTest } from '../api/assessments-api';
import { EXAM_SESSIONS_KEY, examSessionsApi } from '../api/exam-sessions-api';

/**
 * ASM-07's actions. Bulk advance and reject (F9.16) are the pipeline's own bulk action
 * on the ids this screen picks, so the rules, the report and the 10-minute undo are
 * the pipeline's. Each change shows at once and is held until the refetch lands
 * (CLAUDE.md §5a rules 1 and 3); a failure puts the rows back and says why.
 */
export function useResultsActions(jobId: string) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const send = useSendTest();
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());

  const mark = useCallback((ids: readonly string[], isOn: boolean) => {
    setPending((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (isOn) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const settle = useCallback(
    async (ids: readonly string[]): Promise<void> => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: resultsKey(jobId) }), queryClient.invalidateQueries({ queryKey: pipelineKey(jobId) })]);
      mark(ids, false);
    },
    [queryClient, jobId, mark],
  );

  const undo = useCallback(
    async (actionId: string): Promise<void> => {
      try {
        const view = await undoAction(actionId);
        await Promise.all([queryClient.invalidateQueries({ queryKey: resultsKey(jobId) }), queryClient.invalidateQueries({ queryKey: pipelineKey(jobId) })]);
        show({ tone: view.undoRefusals.length === 0 ? 'info' : 'warning', message: `Undone — ${view.undoneCount} put back` });
      } catch (error) {
        show({ tone: 'danger', message: "Couldn't undo that", detail: errorMessage(error) });
      }
    },
    [queryClient, jobId, show],
  );

  /** `note` adds a word the person should see, such as Advance's "2 haven't passed" (R8). */
  const announce = useCallback(
    (view: PipelineActionView, note: string | null): void => {
      if (view.failedCount > 0) {
        const first = view.failures.slice(0, 2).map((failure) => `${failure.candidateName}: ${failure.message}`);
        show({
          tone: 'warning',
          message: bulkResultMessage(view),
          detail: first.join(' '),
          action: { label: 'See details', run: () => void navigate({ to: '/jobs/$jobId/pipeline', params: { jobId }, search: { action: view.id } }) },
        });
        return;
      }
      const message = note === null ? bulkResultMessage(view) : `${bulkResultMessage(view)} ${note}`;
      if (view.canUndo) showUndo(message, () => void undo(view.id));
      else show({ tone: 'success', message });
    },
    [show, showUndo, undo, navigate, jobId],
  );

  /** Advance or reject these applications, drawn at once and put back if refused. */
  const runBulk = useCallback(
    async (body: BulkActionRequest, rows: readonly AssessmentResultRow[], to: ApplicationStage, note: string | null = null): Promise<void> => {
      const ids = rows.map((row) => row.applicationId);
      const snapshot = queryClient.getQueryData<AssessmentResultsView>(resultsKey(jobId));
      queryClient.setQueryData<AssessmentResultsView>(resultsKey(jobId), (data) =>
        data ? { ...data, rows: data.rows.map((row) => (ids.includes(row.applicationId) ? { ...row, stage: to, nextAction: null } : row)) } : data,
      );
      mark(ids, true);
      try {
        const view = await startBulkAction(jobId, body);
        await settle(ids);
        if (view.isAsync) {
          show({ tone: 'info', message: `Working through ${view.requestedCount} people`, detail: "You'll get a notification when it's done." });
          return;
        }
        announce(view, note);
      } catch (error) {
        queryClient.setQueryData(resultsKey(jobId), snapshot);
        mark(ids, false);
        show({ tone: 'danger', message: 'Nothing was changed', detail: errorMessage(error) });
      }
    },
    [queryClient, jobId, mark, settle, show, announce],
  );

  const copy = useCallback(
    async (attemptId: string, candidateName: string): Promise<void> => {
      try {
        await copyTestLink(attemptId);
        show({ tone: 'success', message: `Link copied for ${candidateName}`, detail: 'Paste it into WhatsApp, SMS or an email.' });
      } catch (error) {
        show({ tone: 'danger', message: "The link wasn't copied", detail: errorMessage(error) });
      }
    },
    [show],
  );

  /** A first test, a new link or a retake. The link is offered to copy at once (S17 Q3). */
  const sendTest = useCallback(
    async (row: Pick<AssessmentResultRow, 'applicationId' | 'candidateName' | 'version' | 'nextAction'>): Promise<void> => {
      mark([row.applicationId], true);
      try {
        const sent = await send.mutateAsync({ applicationId: row.applicationId, version: row.version });
        await settle([row.applicationId]);
        show({
          tone: 'success',
          message: `${row.nextAction ? TEST_ACTION_LABELS[row.nextAction] : 'Test'} — ${row.candidateName}`,
          detail: sent.isEmailed ? 'The link is on its way by email. You can also copy it.' : 'They have no email address, so copy the link and send it to them.',
          action: { label: 'Copy link', run: () => void copy(sent.attempt.id, row.candidateName) },
        });
      } catch (error) {
        mark([row.applicationId], false);
        show({ tone: 'danger', message: `No test sent to ${row.candidateName}`, detail: errorMessage(error) });
      }
    },
    [send, settle, show, mark, copy],
  );

  /** S18: a seat on one of the job's exam sessions, with its booking email. */
  const book = useCallback(
    async (row: Pick<AssessmentResultRow, 'applicationId' | 'candidateName'>, sessionId: string, sessionName: string): Promise<void> => {
      mark([row.applicationId], true);
      try {
        const outcome = await examSessionsApi.add(sessionId, [row.applicationId]);
        await Promise.all([settle([row.applicationId]), queryClient.invalidateQueries({ queryKey: EXAM_SESSIONS_KEY })]);
        const [refused] = outcome.refused;
        if (refused) show({ tone: 'danger', message: `${row.candidateName} wasn't booked`, detail: refused.reason });
        else show({ tone: 'success', message: `${row.candidateName} booked on ${sessionName}`, detail: 'They were emailed the date and place.' });
      } catch (error) {
        mark([row.applicationId], false);
        show({ tone: 'danger', message: `${row.candidateName} wasn't booked`, detail: errorMessage(error) });
      }
    },
    [mark, settle, queryClient, show],
  );

  /**
   * ADR 0045: "Select" on a passed test when the job has no interview round — the debrief's
   * selection (F24.11), shown at once and held until the refetch lands (§5a rules 1 and 3).
   */
  const select = useCallback(
    async (row: Pick<AssessmentResultRow, 'applicationId' | 'candidateName' | 'version'>): Promise<void> => {
      const snapshot = queryClient.getQueryData<AssessmentResultsView>(resultsKey(jobId));
      queryClient.setQueryData<AssessmentResultsView>(resultsKey(jobId), (data) =>
        data ? { ...data, rows: data.rows.map((item) => (item.applicationId === row.applicationId ? { ...item, stage: 'offer', nextAction: null } : item)) } : data,
      );
      mark([row.applicationId], true);
      try {
        await selectionApi.select(jobId, row.applicationId, row.version);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: resultsKey(jobId) }),
          queryClient.invalidateQueries({ queryKey: selectedKey(jobId) }),
          queryClient.invalidateQueries({ queryKey: pipelineKey(jobId) }),
          queryClient.invalidateQueries({ queryKey: ['jobs'] }),
        ]);
        mark([row.applicationId], false);
        show({ tone: 'success', message: `${row.candidateName} is selected`, detail: 'The recruiter, the requester and the line manager have been told.' });
      } catch (error) {
        queryClient.setQueryData(resultsKey(jobId), snapshot);
        mark([row.applicationId], false);
        show({ tone: 'danger', message: `${row.candidateName} wasn't selected`, detail: errorMessage(error) });
      }
    },
    [queryClient, jobId, mark, show],
  );

  return { pending, runBulk, sendTest, copy, book, select };
}

export function attemptLabel(row: AssessmentResultRow): string {
  if (!row.latest) {
    if (row.nextAction === 'book') return 'Not booked';
    return row.nextAction === null && row.canRecord ? 'No result yet' : 'Not sent';
  }
  if (row.latest.recorded) return `Recorded · ${recordedSourceLabel(row.latest.recorded)}`;
  return attemptStatusLabel(row.latest.status, row.latest.examSession !== null);
}
